"use client";

import * as React from "react";
import {
  AnimatePresence,
  motion,
  useMotionValue,
  useTransform,
  useReducedMotion,
  type PanInfo,
} from "motion/react";
import { cn } from "../lib/cn";
import { Skeleton } from "../primitives/Skeleton";

/**
 * AnimatedList — vertically-stacked list with motion-driven entrance / exit
 * animations, optional auto-add cycling, swipe-to-dismiss, click effects, and
 * fade-edge masks.
 *
 * Adopted from React Bits Pro (`@reactbits-starter/animated-list-tw`) and
 * adapted to pretty's conventions:
 * - All hardcoded palette colors swapped for theme tokens (`bg-card`,
 *   `border-border`, `text-foreground`, `text-destructive`, `bg-foreground/15`).
 *   Renders dark/light cleanly under any consumer's `--background` / `--card`.
 * - Honors `prefers-reduced-motion` — entrance / exit transitions collapse to
 *   instant when the user opts out, the spring "bounce" animation falls back
 *   to the fade tween, and the swipe-dismiss rotation is suppressed.
 * - Forwards a ref to the outer wrapper for layout measurement.
 *
 * Use cases inside the AISquare platform:
 * - The Studio Quick-Filter sidebar (Creator Studio): per-studio pill rail
 *   that animates in on mount and gracefully cycles when filters change.
 * - Aiko surfaces: notification stacks where new whispers slide in and old
 *   ones expire on a timer.
 * - Activity feeds + recent-events panels with bounded `maxItems`.
 *
 * Composition note: rendering interactive children (buttons, links) inside
 * `renderItem` works as expected — the swipe drag only triggers above 100px
 * of horizontal movement, and `clickEffect` is opt-in. The default container
 * is `bg-transparent`; pass `bg-card` or similar via `className` if you want
 * the rail to sit in its own surface.
 *
 *   <AnimatedList
 *     items={notifications}
 *     animationType="slide"
 *     enterFrom="top"
 *     hoverEffect="scale"
 *     fadeEdges
 *     pauseOnHover
 *   />
 */
export interface AnimatedListItem {
  /** Unique identifier — used as React key + passed to dismiss / click callbacks. */
  id: string | number;
  /** Content to display. The default renderer wraps this in a `text-foreground` div; override via `renderItem` for a custom row layout. */
  content: React.ReactNode;
}

export interface AnimatedListProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "onAnimationStart" | "onAnimationEnd" | "onDrag" | "onDragStart" | "onDragEnd"
> {
  /** Array of items to display. Treated as the initial roster when `autoAddDelay > 0` (cycled to populate new entries). */
  items: AnimatedListItem[];
  /** Duration of item entrance / exit animation in seconds. Default 0.4. */
  duration?: number;
  /** Animation easing as a cubic-bezier 4-tuple. Default `[0.25, 0.46, 0.45, 0.94]` (smoothOut). */
  easing?: [number, number, number, number];
  /** Delay between auto-cycled re-adds in ms. `0` disables auto-add. Default 2000. */
  autoAddDelay?: number;
  /** Maximum number of items to keep in the list. Default 10. */
  maxItems?: number;
  /** Vertical alignment of items within the container. Default `"center"`. */
  startFrom?: "top" | "center" | "bottom";
  /** Type of entrance / exit animation. Default `"slide"`. Reduced-motion users always get `"fade"`. */
  animationType?: "slide" | "fade" | "scale" | "bounce" | "blur";
  /** Direction items enter from. Default `"top"`. */
  enterFrom?: "top" | "bottom" | "left" | "right";
  /** Pause auto-cycling while the cursor is over the list. Default `false`. */
  pauseOnHover?: boolean;
  /** Hover effect applied to each item. Default `"none"`. */
  hoverEffect?: "none" | "scale";
  /** Click effect applied to each item. `"ripple"` adds a Material-style burst; `"press"` scales the row down briefly. Default `"none"`. */
  clickEffect?: "none" | "ripple" | "press";
  /** Render edge fades at the top + bottom of the scroll region. Default `true`. The default uses a CSS `mask-image` so the parent surface shows through naturally — pass `fadeColor` only when you specifically want a colored gradient overlay instead. */
  fadeEdges?: boolean;
  /** Fade-edge size in pixels. Default 80. */
  fadeEdgeSize?: number;
  /**
   * **Deprecated path.** When set, renders two colored gradient overlay divs
   * instead of using a transparent mask-fade. Useful only if the consumer
   * needs the fade to paint a *specific* color (rare). Leaving this unset
   * (the default) lets the parent surface show through cleanly via
   * `mask-image` — which is what consumers usually want when the rail sits
   * inside a `bg-card` / `bg-muted` / brand-tinted container. The overlay
   * approach can mismatch the parent surface and render as a visible
   * solid-color band; the mask approach can't.
   */
  fadeColor?: string;
  /** Allow horizontal drag-to-dismiss on each item. Default `false`. */
  swipeToDismiss?: boolean;
  /** Fired when an item is dismissed (via swipe or programmatic removal). */
  onDismiss?: (item: AnimatedListItem) => void;
  /** Fired when an item is clicked (after the click effect, if any). */
  onItemClick?: (item: AnimatedListItem) => void;
  /** Vertical gap between items in pixels. Default 12. */
  itemGap?: number;
  /** Custom item renderer. Default wraps `item.content` in a `text-foreground` div. */
  renderItem?: (item: AnimatedListItem) => React.ReactNode;
  /** Container height. Accepts any CSS length. Default `"600px"`. */
  height?: string | number;
  /**
   * The roster has not arrived yet — renders row silhouettes carrying the real
   * row's card geometry, so the list does not collapse to zero height and then
   * shove the page down when the feed lands.
   *
   * Outranks `empty`, and the distinction is load-bearing for this component
   * in particular: `items` is also drained by `onDismiss`, so a genuinely
   * empty list is a state the user ARRIVED at, while this one is a state they
   * are waiting on. Telling them "nothing here" mid-fetch invites them to
   * leave a feed that was one tick from filling.
   */
  loading?: boolean;
  /** Silhouette count while `loading`. Default 4. */
  loadingRows?: number;
  /**
   * Rendered when the list is genuinely empty — either nothing was passed, or
   * the user dismissed the last row. Omitted by default; a notification rail
   * with nothing in it is legitimately blank on many surfaces.
   */
  empty?: React.ReactNode;
}

interface RippleProps {
  x: number;
  y: number;
  reduced: boolean;
  onComplete: () => void;
}

const Ripple: React.FC<RippleProps> = ({ x, y, reduced, onComplete }) => {
  // Reduced-motion users skip the expanding burst entirely. We still call
  // `onComplete` after a short delay so the parent's ripple-tracking state
  // doesn't accumulate.
  React.useEffect(() => {
    if (!reduced) return;
    const id = window.setTimeout(onComplete, 150);
    return () => window.clearTimeout(id);
  }, [reduced, onComplete]);

  if (reduced) return null;

  return (
    <motion.span
      className="pointer-events-none absolute rounded-full bg-foreground/15"
      style={{ left: x, top: y, x: "-50%", y: "-50%" }}
      initial={{ width: 0, height: 0, opacity: 0.5 }}
      animate={{ width: 300, height: 300, opacity: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      onAnimationComplete={onComplete}
    />
  );
};

interface AnimatedListItemComponentProps {
  item: AnimatedListItem;
  itemRenderer: (item: AnimatedListItem) => React.ReactNode;
  hoverEffect: "none" | "scale";
  clickEffect: "none" | "ripple" | "press";
  swipeToDismiss: boolean;
  reduced: boolean;
  onDismiss?: (item: AnimatedListItem) => void;
  onItemClick?: (item: AnimatedListItem) => void;
}

const AnimatedListItemComponent: React.FC<AnimatedListItemComponentProps> = ({
  item,
  itemRenderer,
  hoverEffect,
  clickEffect,
  swipeToDismiss,
  reduced,
  onDismiss,
  onItemClick,
}) => {
  const [ripples, setRipples] = React.useState<{ id: number; x: number; y: number }[]>([]);
  const [isPressed, setIsPressed] = React.useState(false);
  const rippleIdRef = React.useRef(0);
  const itemRef = React.useRef<HTMLDivElement>(null);

  const x = useMotionValue(0);
  const opacity = useTransform(x, [-150, 0, 150], [0, 1, 0]);
  // Suppress the swipe rotation when reduced-motion is on; the dismiss still
  // triggers but the visual sleight is not announced.
  const rotateZ = useTransform(x, [-150, 0, 150], reduced ? [0, 0, 0] : [-10, 0, 10]);
  const swipeIndicatorOpacity = useTransform(x, [-100, -50, 50, 100], [1, 0, 0, 1]);

  const handleDragEnd = (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    if (Math.abs(info.offset.x) > 100 && swipeToDismiss) {
      onDismiss?.(item);
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (clickEffect === "ripple" && itemRef.current) {
      const rect = itemRef.current.getBoundingClientRect();
      const id = rippleIdRef.current++;
      setRipples((prev) => [...prev, { id, x: e.clientX - rect.left, y: e.clientY - rect.top }]);
    }
    onItemClick?.(item);
  };

  const removeRipple = (id: number) => {
    setRipples((prev) => prev.filter((r) => r.id !== id));
  };

  return (
    <motion.div
      ref={itemRef}
      className={cn(
        "relative overflow-hidden rounded-2xl border border-border bg-card",
        onItemClick && "cursor-pointer",
      )}
      drag={swipeToDismiss ? "x" : false}
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.5}
      onDragEnd={handleDragEnd}
      onClick={handleClick}
      onMouseDown={() => clickEffect === "press" && setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
      onMouseLeave={() => setIsPressed(false)}
      animate={{ scale: isPressed && !reduced ? 0.97 : 1 }}
      whileHover={hoverEffect === "scale" && !reduced ? { scale: 1.02 } : undefined}
      transition={{ duration: reduced ? 0 : 0.15 }}
    >
      {swipeToDismiss && (
        <motion.div
          className="pointer-events-none absolute inset-0 flex items-center justify-center"
          style={{ opacity: swipeIndicatorOpacity }}
        >
          <span className="text-sm font-semibold text-destructive">Dismiss</span>
        </motion.div>
      )}

      <motion.div className="p-4" style={swipeToDismiss ? { x, opacity, rotateZ } : undefined}>
        {itemRenderer(item)}
      </motion.div>

      {ripples.map((ripple) => (
        <Ripple
          key={ripple.id}
          x={ripple.x}
          y={ripple.y}
          reduced={reduced}
          onComplete={() => removeRipple(ripple.id)}
        />
      ))}
    </motion.div>
  );
};

export const AnimatedList = React.forwardRef<HTMLDivElement, AnimatedListProps>(
  (
    {
      items: initialItems,
      duration = 0.4,
      easing = [0.25, 0.46, 0.45, 0.94],
      autoAddDelay = 2000,
      maxItems = 10,
      startFrom = "center",
      animationType = "slide",
      enterFrom = "top",
      pauseOnHover = false,
      hoverEffect = "none",
      clickEffect = "none",
      fadeEdges = true,
      fadeEdgeSize = 80,
      fadeColor,
      swipeToDismiss = false,
      onDismiss,
      onItemClick,
      itemGap = 12,
      className,
      renderItem,
      height = "600px",
      loading = false,
      loadingRows = 4,
      empty,
      style,
      ...props
    },
    ref,
  ) => {
    const reduced = useReducedMotion() ?? false;
    const [items, setItems] = React.useState<AnimatedListItem[]>(() => initialItems);
    const itemIndexRef = React.useRef(initialItems.length);
    const intervalRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
    const [isPaused, setIsPaused] = React.useState(false);

    // Keep the visible list in sync if the consumer mutates the source array
    // (e.g. updating notifications from a query).
    React.useEffect(() => {
      setItems(initialItems);
      itemIndexRef.current = initialItems.length;
    }, [initialItems]);

    React.useEffect(() => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      // `loading` stops the clock too. The usual loading case has an empty
      // roster and is already covered, but a surface showing stale rows while
      // it refetches would otherwise keep prepending cycled items behind the
      // placeholders — invisible as they arrive, then all at once when the
      // flag clears.
      if (autoAddDelay <= 0 || initialItems.length === 0 || loading) return;

      intervalRef.current = setInterval(() => {
        if (isPaused) return;
        setItems((prev) => {
          const sourceItem = initialItems[itemIndexRef.current % initialItems.length];
          const newItem: AnimatedListItem = {
            id: `${sourceItem.id}-${itemIndexRef.current}`,
            content: sourceItem.content,
          };
          itemIndexRef.current++;
          return [newItem, ...prev].slice(0, maxItems);
        });
      }, autoAddDelay);

      return () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
      };
    }, [autoAddDelay, maxItems, initialItems, isPaused, loading]);

    const handleDismiss = React.useCallback(
      (item: AnimatedListItem) => {
        setItems((prev) => prev.filter((i) => i.id !== item.id));
        onDismiss?.(item);
      },
      [onDismiss],
    );

    const defaultRenderItem = React.useCallback(
      (item: AnimatedListItem) => <div className="text-foreground">{item.content}</div>,
      [],
    );
    const itemRenderer = renderItem ?? defaultRenderItem;

    // Reduced-motion gets a uniform fade — bouncy springs / large translates
    // are explicitly suppressed.
    const effectiveAnimationType = reduced ? "fade" : animationType;

    const getInitialAnimation = () => {
      const base: Record<string, number | string> = { height: 0, opacity: 0 };
      if (!reduced) {
        switch (enterFrom) {
          case "left":
            base.x = -50;
            break;
          case "right":
            base.x = 50;
            break;
          case "bottom":
            base.y = 30;
            break;
          case "top":
          default:
            base.y = -30;
            break;
        }
      }
      switch (effectiveAnimationType) {
        case "scale":
          base.scale = 0.8;
          break;
        case "blur":
          base.filter = "blur(10px)";
          break;
      }
      return base;
    };

    const getAnimateState = () => {
      const base: Record<string, number | string> = { height: "auto", opacity: 1, x: 0, y: 0 };
      if (effectiveAnimationType === "scale") base.scale = 1;
      if (effectiveAnimationType === "blur") base.filter = "blur(0px)";
      return base;
    };

    const getExitAnimation = () => {
      const base: Record<string, number | string> = {
        height: 0,
        opacity: 0,
        y: reduced ? 0 : 20,
      };
      if (effectiveAnimationType === "scale") base.scale = 0.8;
      if (effectiveAnimationType === "blur") base.filter = "blur(10px)";
      return base;
    };

    const getTransition = () => {
      if (reduced) return { duration: 0 };
      const base = { duration, ease: easing };
      if (effectiveAnimationType === "bounce") {
        return {
          ...base,
          type: "spring" as const,
          bounce: 0.4,
          stiffness: 300,
          damping: 20,
        };
      }
      return base;
    };

    const paddingClass =
      startFrom === "center" ? "pt-[50%]" : startFrom === "bottom" ? "pt-[100%]" : "";

    // Two ways to render the edge fade:
    //   1. `mask-image` on the scroll container (default). The content fades to
    //      transparent at the edges so the parent surface shows through. This
    //      works regardless of what color sits behind the rail (`bg-card` /
    //      `bg-muted` / brand-tinted) — no configuration burden.
    //   2. Colored gradient overlays (when `fadeColor` is set). Legacy path,
    //      kept for consumers who want the fade to paint a specific color.
    //      Mismatching the parent surface here renders as a visible band — see
    //      the prior bug report where the default `hsl(var(--background))`
    //      origin showed as a square dark patch over a `bg-card` rail.
    const useColoredOverlay = fadeEdges && Boolean(fadeColor);
    const useMaskFade = fadeEdges && !fadeColor;

    const maskImage = useMaskFade
      ? `linear-gradient(to bottom, transparent 0px, black ${fadeEdgeSize}px, black calc(100% - ${fadeEdgeSize}px), transparent 100%)`
      : undefined;

    return (
      <div
        ref={ref}
        className={cn("relative w-full overflow-hidden", className)}
        style={{ height, ...style }}
        aria-busy={loading || undefined}
        {...props}
      >
        {useColoredOverlay && (
          <>
            <div
              aria-hidden="true"
              className="pointer-events-none absolute left-0 right-0 top-0 z-10"
              style={{
                height: fadeEdgeSize,
                background: `linear-gradient(to bottom, ${fadeColor} 0%, transparent 100%)`,
              }}
            />
            <div
              aria-hidden="true"
              className="pointer-events-none absolute bottom-0 left-0 right-0 z-10"
              style={{
                height: fadeEdgeSize,
                background: `linear-gradient(to top, ${fadeColor} 0%, transparent 100%)`,
              }}
            />
          </>
        )}

        <div
          className="h-full overflow-y-auto overflow-x-hidden [&::-webkit-scrollbar]:hidden [scrollbar-width:none]"
          style={
            maskImage
              ? {
                  maskImage,
                  WebkitMaskImage: maskImage,
                }
              : undefined
          }
          onMouseEnter={() => pauseOnHover && setIsPaused(true)}
          onMouseLeave={() => pauseOnHover && setIsPaused(false)}
        >
          {!loading && items.length === 0 ? (
            // Not a list any more — rendering the slot as an <li> would put an
            // "add your first one" panel inside a list of one thing.
            <div className="flex h-full items-center justify-center p-4">{empty}</div>
          ) : (
            <ul
              className={cn("p-4", paddingClass)}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: `${itemGap}px`,
              }}
            >
              <AnimatePresence initial={false} mode="popLayout">
                {loading
                  ? Array.from({ length: Math.max(1, loadingRows) }).map((_, i) => (
                      <li key={`skeleton-${i}`} aria-hidden="true">
                        <div className="rounded-2xl border border-border bg-card p-4">
                          <div className="space-y-2">
                            <Skeleton
                              variant="text"
                              animation="shimmer"
                              className="h-3.5 w-[70%]"
                            />
                            <Skeleton
                              variant="text"
                              animation="shimmer"
                              className="h-3.5 w-[45%]"
                            />
                          </div>
                        </div>
                      </li>
                    ))
                  : items.map((item) => (
                      <motion.li
                        key={item.id}
                        layout={!reduced}
                        initial={getInitialAnimation()}
                        animate={getAnimateState()}
                        exit={getExitAnimation()}
                        transition={getTransition()}
                        style={{ overflow: "visible", willChange: "transform, opacity, height" }}
                      >
                        <AnimatedListItemComponent
                          item={item}
                          itemRenderer={itemRenderer}
                          hoverEffect={hoverEffect}
                          clickEffect={clickEffect}
                          swipeToDismiss={swipeToDismiss}
                          reduced={reduced}
                          onDismiss={handleDismiss}
                          onItemClick={onItemClick}
                        />
                      </motion.li>
                    ))}
              </AnimatePresence>
            </ul>
          )}
        </div>
      </div>
    );
  },
);
AnimatedList.displayName = "AnimatedList";
